import { Inject, Injectable } from '@angular/core';
import { AlarisLocalStorageService, TokenStatus } from '@campaign-portal/components-library';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
	AuthIsPasswordCorrectRequest,
	AuthIsPasswordCorrectResponse,
	AuthSignInResponse,
	AuthTokenResponse,
} from '@helpers/types/user-and-roles';
import { UserService } from './user.service';
import { RepoService } from '@helpers/repo/repo.service';
import { STORAGE, TOKEN_REQUEST_TIME } from '@helpers/types/consts';
import { TokenData, Author } from '@helpers/types/models';
import { USERS_AND_ROLES_API } from '@helpers/shared/injection-tokens';
import { HlrPaths } from '@helpers/shared/hlr-paths';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	readonly loading$ = new BehaviorSubject<boolean>(false);
	private readonly tokenHelper: JwtHelperService;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly storage: AlarisLocalStorageService,
		private readonly user: UserService,
		private readonly repo: RepoService,
		@Inject(USERS_AND_ROLES_API) private readonly userAndRolesApi: { url: string }
	) {
		this.tokenHelper = new JwtHelperService();
	}

	get logged(): boolean {
		try {
			const token = this.storage.get(STORAGE.TOKEN);
			const detailsRaw = this.storage.get(STORAGE.USER_DETAILS);

			if (token && !this.user.user.id && !this.tokenHelper.isTokenExpired(token)) {
				const tokenData: TokenData | null = this.tokenHelper.decodeToken(token);
				if (!tokenData) throw new Error('unable to parse auth token');

				const details = detailsRaw ? JSON.parse(detailsRaw) : undefined;

				this.user.register({
					email: tokenData.email,
					id: +tokenData.jti,
					login: tokenData.sub,
					permissions: tokenData.permissions?.split(',') || [],
					details,
				});
			}

			return token ? !this.tokenHelper.isTokenExpired(token) : false;
		} catch (e) {
			console.error('Invalid token', this.storage.get(STORAGE.TOKEN), e);
			return false;
		}
	}

	logIn(login: string, password: string, passcode?: string): Observable<AuthSignInResponse> {
		this.loading$.next(true);
		let params;
		if (!passcode) {
			params = {
				Login: login,
				Password: password,
			};
		} else {
			params = {
				Login: login,
				Password: password,
				Passcode: passcode,
			};
		}
		return this.http
			.post<AuthSignInResponse>(this.userAndRolesApi.url, {
				method: 'Auth.SignIn',
				params,
			})
			.pipe(
				tap((resp) => {
					this.setAuthData(resp);
					this.router.navigate(['']).then();
				})
			);
	}

	logOut(): void {
		this.resetAuth();
		this.router.navigate([HlrPaths.SignIn]);
	}

	isPasswordCorrect(params: AuthIsPasswordCorrectRequest): Observable<AuthIsPasswordCorrectResponse> {
		return this.http.post<AuthIsPasswordCorrectResponse>(this.userAndRolesApi.url, {
			method: 'Auth.IsPasswordCorrect',
			params,
		});
	}

	resetAuth(): void {
		this.storage.remove(STORAGE.TOKEN);
		this.storage.remove(STORAGE.USER_DETAILS);
		this.repo.reset();
		this.user.reset();
	}

	refreshToken(): Observable<AuthTokenResponse> {
		return this.http.post<AuthSignInResponse>(this.userAndRolesApi.url, { method: 'Auth.Token' }).pipe(
			tap((resp) => {
				if (resp.JWT) this.storage.set(STORAGE.TOKEN, resp.JWT);
			})
		);
	}

	checkTokenExpired(token: string | null): TokenStatus {
		if (!token) return TokenStatus.EMPTY;
		const tokenData: TokenData | null = this.tokenHelper.decodeToken(token);
		const expMs = (tokenData?.exp ?? 0) * 1000;
		const diff = expMs - new Date().getTime();
		const expired = diff < TOKEN_REQUEST_TIME;
		return expired ? TokenStatus.EXPIRED : TokenStatus.ACTIVE;
	}

	private setAuthData(resp: AuthSignInResponse): void {
		if (!resp.JWT) return;
		this.storage.set(STORAGE.TOKEN, resp.JWT);
		const details = resp.User.Details ?? {};
		this.storage.set(STORAGE.USER_DETAILS, JSON.stringify(details));

		const userData: Author = {
			email: (details['email'] as string) ?? '',
			id: (details['id'] as number) ?? 0,
			login: (details['login'] as string) ?? '',
			permissions: (details['permissions'] as string[]) ?? [],
			details: details,
		};
		this.user.register(userData);
	}
}
